import { z } from "zod";

/**
 * Ce que l'appli compagnon mesure d'une partie, envoye a `POST /api/mesure` (issue #242, test
 * #236). La forme est fermee : le backend refuse tout champ de plus.
 *
 * Aucune donnee personnelle : un identifiant de livre et de scene, un numero de page, des
 * compteurs. Ni la reponse tapee a une enigme, ni le code de sauvegarde, ni un identifiant du
 * lecteur ou de sa partie : chaque mesure part seule, sans rien qui la relie aux autres.
 */

/**
 * Identifiant d'un livre ou d'une scene, comme le format des histoires les ecrit. Le contrat du
 * livre publie (`livre.ts`) reprend le meme : un livre que l'appli accepte donne des mesures que
 * le backend accepte.
 */
export const identifiant = z.string().regex(/^[a-z0-9][a-z0-9-]{0,59}$/);
const page = z.int().min(1).max(999);
const compteur = z.int().min(0).max(9999);

/** Comment le code est arrive : scanner de l'appli, appareil photo du telephone, ou tape. */
export const VIAS = ["scan", "lien", "saisie"] as const;
export type Via = (typeof VIAS)[number];

/**
 * Raisons d'un refus : celles du moteur (`Refus` dans `moteur/moteur.ts`), plus
 * `pas-un-code`, un QR code qui ne vient pas du livre.
 */
export const RAISONS_DE_REFUS = [
  "code-inconnu",
  "pas-sur-cette-page",
  "partie-finie",
  "enigme-attendue",
  "pas-une-enigme",
  "aucune-suite",
  "etat-perime",
  "pas-un-code",
] as const;
export type RaisonDeRefus = (typeof RAISONS_DE_REFUS)[number];

/** Raisons d'un code de sauvegarde refuse : celles de `moteur/sauvegarde.ts`. */
export const REFUS_DE_SAUVEGARDE = [
  "longueur",
  "caractere",
  "controle",
  "version",
  "autre-livre",
  "hors-limites",
] as const;

const via = z.enum(VIAS);

export const mesureSchema = z.discriminatedUnion("quoi", [
  /** Une partie commence : premier choix applique, apres un depart ou un « Recommencer ». */
  z.strictObject({ quoi: z.literal("partie"), livre: identifiant }),
  /** Le telephone donne une page : `scene` est la scene d'arrivee. */
  z.strictObject({ quoi: z.literal("choix"), livre: identifiant, scene: identifiant, page, via }),
  /** Un code refuse : `scene` est celle ou en est le lecteur, la ou le livre l'a perdu. */
  z.strictObject({
    quoi: z.literal("refus"),
    livre: identifiant,
    scene: identifiant,
    raison: z.enum(RAISONS_DE_REFUS),
    via,
  }),
  /** Une reponse a une enigme : `scene` est celle qui la pose. */
  z.strictObject({
    quoi: z.literal("enigme"),
    livre: identifiant,
    scene: identifiant,
    juste: z.boolean(),
    via,
  }),
  /** La partie atteint une fin, une seule fois par partie. `minutes` depuis son debut. */
  z.strictObject({
    quoi: z.literal("fin"),
    livre: identifiant,
    type: z.enum(["reussie", "ratee"]),
    minutes: z.int().min(0).max(100_000),
    coups: compteur,
    refus: compteur,
    saisis: compteur,
  }),
  /** Le lecteur a demande son code de sauvegarde. */
  z.strictObject({ quoi: z.literal("sauvegarde"), livre: identifiant }),
  z.strictObject({
    quoi: z.literal("reprise"),
    livre: identifiant,
    ok: z.boolean(),
    raison: z.enum(REFUS_DE_SAUVEGARDE).optional(),
  }),
]);
export type Mesure = z.infer<typeof mesureSchema>;
